import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlaneLanding, Check, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";
import { deDayMonth } from "../../lib/dateFormat";

const TRIGGER_DAYS = 7;
const EMPTY_CHECKS = {};

/**
 * Countdown-Widget für die Anreise-Checkliste.
 * Zeigt sich automatisch, wenn das Rückreise-Datum (`to_date`) der nächsten aktiven Buchung
 * in ≤ 7 Tagen liegt UND der Kunde eine individuell gepflegte Anreise-Checkliste hat.
 * Ohne DB-Items rendert das Widget garnichts (keine Beispiel-Fallbacks).
 * Fortschritt wird per booking-Id serverseitig gemerkt (users.ui_prefs.arrival_checklist).
 */
export function ArrivalCountdownWidget({ bookings, propertiesById }) {
  const { lang } = useI18n();
  const { uiPrefs, setUiPrefs } = useAuth();
  const de = lang === "de";
  const nextArrival = useMemo(() => {
    if (!bookings || bookings.length === 0) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const candidates = bookings
      .filter((b) => b.to_date && b.status !== "cancelled" && b.status !== "completed")
      .map((b) => {
        const d = new Date(b.to_date);
        d.setHours(0, 0, 0, 0);
        const days = Math.round((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        return { ...b, days };
      })
      .filter((b) => b.days >= 0 && b.days <= TRIGGER_DAYS)
      .sort((a, b) => a.days - b.days);
    return candidates[0] || null;
  }, [bookings]);

  const [items, setItems] = useState(null); // null = loading, [] = keine DB-items → Widget nicht rendern
  const bookingId = nextArrival?.id || null;
  const checks = (bookingId && uiPrefs?.arrival_checklist?.[bookingId]) || EMPTY_CHECKS;

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/checklist");
        if (cancelled) return;
        const arr = Array.isArray(data?.arrival_items) ? data.arrival_items : [];
        setItems(arr);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  const toggle = (key) => {
    if (!bookingId) return;
    const next = { ...checks, [key]: !checks[key] };
    const all = { ...(uiPrefs?.arrival_checklist || {}), [bookingId]: next };
    setUiPrefs({ arrival_checklist: Object.fromEntries(Object.entries(all).slice(-10)) });
  };

  if (!nextArrival) return null;
  // Ohne individuell hinterlegte Anreise-Checkliste rendern wir keinen
  // statischen Platzhalter — der Kunde soll nur echte, DB-gepflegte
  // Aufgaben sehen.
  if (items === null || items.length === 0) return null;

  const done = items.filter((it) => checks[it.key]).length;
  const total = items.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const daysLabel =
    nextArrival.days === 0 ? (de ? "Heute" : "Today") :
    nextArrival.days === 1 ? (de ? "Morgen" : "Tomorrow") :
    (de ? `In ${nextArrival.days} Tagen` : `In ${nextArrival.days} days`);
  const property = propertiesById?.[nextArrival.property_id];

  return (
    <section
      className="relative overflow-hidden mt-6 border border-gold/30"
      style={{ background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory }}
      data-testid="arrival-countdown-widget"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: withAlpha(BRAND_COLORS.gold, 0.18) }}>
              <PlaneLanding className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="label-caps mb-0.5" style={{ color: BRAND_COLORS.gold }}>
                {de ? "Ihre Anreise" : "Your return"} · {daysLabel}
              </div>
              <div className="font-display text-2xl md:text-[26px] leading-tight truncate">
                {deDayMonth(nextArrival.to_date)}
                {property?.name && <span className="text-ivory/60"> · {property.name}</span>}
              </div>
            </div>
          </div>
          <div
            className="text-[11px] tracking-widest uppercase px-2.5 py-1 rounded-full shrink-0"
            style={{ background: withAlpha(BRAND_COLORS.ivory, 0.12), color: BRAND_COLORS.ivory }}
            data-testid="arrival-countdown-progress-pill"
          >
            {done} / {total}
          </div>
        </div>

        <p className="text-[13px] text-ivory/75 leading-relaxed mb-4">
          {de
            ? `So findet Ihr Team alles vor Ihrer Rückkehr am ${deDayMonth(nextArrival.to_date)}:`
            : `Here is how your team makes everything ready for your return on ${deDayMonth(nextArrival.to_date)}:`}
        </p>

        <ul className="space-y-1.5" data-testid="arrival-countdown-list">
          {items.map((item) => {
            const isDone = !!checks[item.key];
            return (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => toggle(item.key)}
                  aria-pressed={isDone}
                  data-testid={`arrival-countdown-item-${item.key}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/5 rounded"
                >
                  <span
                    className="w-5 h-5 border flex items-center justify-center shrink-0 transition-colors"
                    style={{
                      background: isDone ? BRAND_COLORS.gold : "transparent",
                      borderColor: isDone ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.ivory, 0.4),
                    }}
                  >
                    {isDone && <Check className="w-3 h-3" style={{ color: BRAND_COLORS.navy }} strokeWidth={3} />}
                  </span>
                  <span
                    className={`text-[13.5px] leading-snug ${isDone ? "line-through" : ""}`}
                    style={{ color: isDone ? withAlpha(BRAND_COLORS.ivory, 0.5) : BRAND_COLORS.ivory }}
                  >
                    {item.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Progress bar */}
        <div className="mt-4 h-1 rounded-full overflow-hidden" style={{ background: withAlpha(BRAND_COLORS.ivory, 0.1) }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, background: BRAND_COLORS.gold }}
            data-testid="arrival-countdown-progress-bar"
          />
        </div>

        <Link
          to="/app/arrival"
          data-testid="arrival-countdown-cta"
          className="mt-5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase pb-1 border-b transition-colors"
          style={{ color: BRAND_COLORS.gold, borderColor: withAlpha(BRAND_COLORS.gold, 0.5) }}
        >
          {de ? "Zur vollständigen Ab-/Anreise" : "Full departure & return"}
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}
